// Buffy's pointing vocabulary: every spot the AI assistant may spotlight, keyed
// by page + step id. Targets are data-tour attributes already on the pages.
// The lambda prompt carries a projection of this list (lambda/claude-sandy-tutor/
// prompts/tours.md); keep the two in sync and redeploy the lambda when ids change.
// `path: null` = the target exists on every dashboard page (shell chrome).
// 2026-09-23 audit: removed Insights (the route redirects to Home), the legacy
// Home targets, Assignments "Start this domain" (never rendered now) and the
// Gradebook practice-sets section (practice sets are columns in the grade
// table); added Create Assignments and the Students page. The spotlight opens a
// collapsed <details> section around its target.

export const TOUR_REGISTRY = {
  shell: {
    path: null,
    label: 'every page',
    steps: {
      'class-switcher': {
        target: 'class',
        title: 'Class switcher',
        body: 'Switch between your classes here. The class you pick follows you across every page. The menu also has the join code, Manage class and New class.',
      },
    },
  },
  assignments: {
    path: '/teacher-dashboard/assignments',
    label: 'Practice',
    steps: {
      'start-unit': {
        target: 'start-unit',
        title: 'Start a unit',
        body: 'Start a unit here. Tell Buffy what you want in your own words and it drafts it with you; nothing goes to students until you confirm.',
      },
      'new-assignment': {
        target: 'new-assignment',
        title: 'Assign practice',
        body: 'Make a practice set here. Tell Buffy what you want in your own words and it drafts it with you; nothing goes to students until you confirm.',
      },
      'unit-timeline': {
        target: 'plan',
        title: 'Unit checkpoints',
        body: 'The running unit’s checkpoint due dates. Click any due date to change it; saving updates student due dates right away.',
      },
      'finish-unit': {
        target: 'finish-unit',
        title: 'Finish unit & lock grades',
        body: 'The normal end of a unit: saves every student’s grades as they stand and closes the unit. You decide when; it is not tied to the due dates.',
      },
    },
  },
  monitor: {
    path: '/teacher-dashboard/monitor',
    label: 'Live Monitor',
    steps: {
      'start-session': {
        target: 'start',
        title: 'Start a live session',
        body: 'Press this when work time begins. The session ends on its own when you leave Brain Buffs.',
      },
      'attention-groups': {
        target: 'groups',
        title: 'The open assessment',
        body: 'How many are taking it, done, off task or not started yet. With no open assessment: working, needs you and not started.',
      },
      'live-roster': {
        target: 'live-table',
        title: 'Every student, live',
        body: 'What each student is doing, how far along they are, their last action and one flag. Who needs you comes first; click a flag to see why. Use "Find a student" to filter.',
      },
      'end-session': {
        target: 'end',
        title: 'End',
        body: 'Ends the live session when class wraps up. Only visible while a session is running.',
      },
    },
  },
  intervene: {
    path: '/teacher-dashboard/intervene',
    label: 'Intervene',
    steps: {
      'stuck-list': {
        target: 'rail',
        title: 'Students who need a check review',
        body: 'Students who failed a skill check, grouped by level. Click one to see exactly what they missed.',
      },
      'student-detail': {
        target: 'panel',
        title: 'Student detail',
        body: 'The questions they missed with their pick vs the right answer, plus the release buttons at the bottom when you have retaught it.',
      },
    },
  },
  gradebook: {
    path: '/teacher-dashboard/gradebook',
    label: 'Gradebook',
    steps: {
      'view-toggle': {
        target: 'views',
        title: 'Units',
        body: 'Overview, then one tab per unit, including past ones; pick a unit to see its grades. The current unit is marked on its tab, and the key under the sheet explains the score colors.',
      },
      'adjust-grading': {
        target: 'grading',
        title: 'Adjust grading',
        body: 'The grading levers: the suspected-cheating and clicking-through multipliers, plus the late penalty. Pick a unit first; changes apply until the unit’s grades lock.',
      },
      'grade-table': {
        target: 'table',
        title: 'Grade table',
        body: 'One row per student: a column per checkpoint, the unit grade, then any curriculum-assessment columns and the practice-set columns, both graded on right answers. Click a cell for the details; click the unit grade to set a manual grade.',
      },
    },
  },
  students: {
    path: '/teacher-dashboard/students',
    label: 'Students',
    steps: {
      'week-picker': {
        target: 'week',
        title: 'Practice week',
        body: 'Pick a week to see each student’s practice minutes and questions for that week, up to 8 weeks back.',
      },
      'roster': {
        target: 'roster',
        title: 'Your students',
        body: 'Sort by name, practice or last active. Click a student for their full overview; the key icon resets a password.',
      },
    },
  },
};

// Resolve a page/step pair from a model tag. Returns null for anything the
// registry does not know; the chat drops unknown tags.
// A target that is not on the page yet (2026-10-23: the unit plan's dates and
// Finish live in the unit's pop-up on Assignments): the control that shows it
// (a tab, or the unit's Open) lists the target in data-tour-reveals. Clicks it
// and says whether it did; the target renders next frame.
export function revealTourTarget(target) {
  if (!target || typeof document === 'undefined') return false;
  const tab = [...document.querySelectorAll('[data-tour-reveals]')].find(t => t.getAttribute('data-tour-reveals').split(' ').includes(target));
  if (!tab || tab.getAttribute('aria-selected') === 'true') return false;
  tab.click();
  return true;
}

export function findTourStep(page, step) {
  const p = TOUR_REGISTRY[page];
  const s = p?.steps?.[step];
  return s ? { ...s, page, step, path: p.path, pageLabel: p.label } : null;
}
